/** @jsxImportSource theme-ui */
import { useBreakpointIndex } from '@theme-ui/match-media';
import classNames from 'classnames';
import React, { useContext, useEffect, useState } from 'react';
import { Icon, Menu } from 'semantic-ui-react';

import { AppStateContext } from 'api/AppStateContext';
import { useInsightsRouting } from 'hooks/useInsightsRouting';
import { TimeframeSelectionResult } from 'models/TimeframeModels';
import { isNonMobile } from 'utils/responsive';

import { BdIcon } from 'components/_common/BdIcon';
import { FlexGroup } from 'components/_common/FlexGroup';
import { BdTooltip } from 'components/_controls/BdTooltip';

import { TimeframeDropdown } from './TimeframeDropdown';

/* eslint-disable react/jsx-wrap-multilines */
const PanelCollapse: React.FC = () => {
  const { appState, amendState } = useContext(AppStateContext);
  const { isSidebarCollapsed } = appState;

  const handleToggleButtonOnClick = () => {
    amendState({
      isSidebarCollapsed: !isSidebarCollapsed,
    });
  };

  useEffect(() => {
    amendState({ isSidebarCollapsed: false });
  }, [amendState]);

  const collapseChevronProps = {
    sx: {
      '&.icon.bd-icon': {
        fontSize: 'small',
        fontWeight: 'bold',
      },
      'i.icons &.icon:first-child': {
        verticalAlign: 'middle',
        m: 0,
      },
    },
  };

  const sharedIconProps = {
    sx: {
      'i.icons &.icon.bd-icon': {
        verticalAlign: 'middle',
        m: 0,
      },
    },
  };

  const isNonMobileDevice = isNonMobile(useBreakpointIndex());

  return (
    <div
      id="sidebar"
      data-testid="sidebar"
      className={classNames({
        'bd-animation-slide-out': isSidebarCollapsed === true,
        'bd-animation-slide-in': isSidebarCollapsed === false,
      })}
    >
      {isNonMobileDevice && (
        <Icon.Group
          onClick={handleToggleButtonOnClick}
          sx={{
            cursor: 'pointer',
            alignSelf: 'start',
            '&:focus': {},
          }}
        >
          <BdTooltip
            position="left center"
            text={isSidebarCollapsed ? 'Show panels' : 'Hide panels'}
          >
            <FlexGroup
              gutter="4px"
              alignItems="flex-start"
              prefix={
                !isSidebarCollapsed && (
                  <BdIcon
                    nomargin
                    bold
                    {...collapseChevronProps}
                    name="icon-chevron-left"
                    color="deepSea90"
                  />
                )
              }
              suffix={
                isSidebarCollapsed && (
                  <BdIcon
                    nomargin
                    bold
                    {...collapseChevronProps}
                    name="icon-chevron-right"
                    color="deepSea90"
                  />
                )
              }
            >
              <BdIcon nomargin name="icon-maps" bold color="deepSea90" {...sharedIconProps} />
            </FlexGroup>
          </BdTooltip>
        </Icon.Group>
      )}
    </div>
  );
};

const DashboardToolbar = ({ showToolbar, showPanelCollapse = true }) => {
  const { appState } = useContext(AppStateContext);
  const { setStartTime } = useInsightsRouting();

  const isNonMobileDevice = isNonMobile(useBreakpointIndex());

  const [, setIsOpen] = useState(false);

  const handleDateSelected = (dateSelection: TimeframeSelectionResult) => {
    setIsOpen(false);
    if (dateSelection.isCustom) {
      setStartTime(dateSelection.customDateStr);
    } else {
      setStartTime(dateSelection.preset);
    }
  };

  return (
    <div className="dashboard-toolbar-wrapper" sx={{ display: showToolbar ? 'block' : 'none' }}>
      <Menu borderless attached="bottom" className="dashboard__toolbar">
        {isNonMobileDevice ? (
          <>
            <Menu.Item position="left">{showPanelCollapse && <PanelCollapse />}</Menu.Item>
            <Menu.Item>
              <TimeframeDropdown
                dateStr={appState.startDate}
                onDateSelectionChanged={handleDateSelected}
                onCancel={() => {
                  setIsOpen(false);
                }}
              />
            </Menu.Item>
            <Menu.Item position="right">
              {/* ghost menu item for maintaining alignment  */}
            </Menu.Item>
          </>
        ) : (
          <TimeframeDropdown
            dateStr={appState.startDate}
            onDateSelectionChanged={handleDateSelected}
            onCancel={() => {
              setIsOpen(false);
            }}
          />
        )}
      </Menu>
    </div>
  );
};

export default DashboardToolbar;
